import React, { useEffect, useState, useRef, useCallback } from 'react';
import './VoiceChatUI.css';
import { StreamPixelVoiceChat } from 'streampixel';
import VideoTilesUI from './VideoTilesUI';
import ChatPanel from './ChatPanel';
import MessageToasts from './MessageToasts';

function isMobilePhone() {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  return /android|iphone|blackberry|iemobile|opera mini/i.test(ua)
    && !/ipad|ipod/i.test(ua);
}

const isMobile = isMobilePhone();
let startMicDone = false;
export default function VoiceChatUI({
  roomName, micStart, roomConnect, roomDisconnect, toggleMicChat, apiBase, voiceToken, livekitUrl,
  onMicStateChange,  // (micOn: boolean) — report the REAL mic state up so the
                     // controlPanel button never drifts from LiveKit's actual state
  handleRoomConnect, handleMessageNumber, userName, voiceChat, textChat,
  videoChat = true,
  darkMode, position, avatar, showChatUiMobile, showChatUiDesktop = true,
  onCloseChatUi, onCloseChatUiDesktop, onOpenChatUi, onOpenChatUiDesktop,
  onTypingChange,  // (boolean) — true while at least one remote is typing
  streamReady = true,  // when false, hide chat/audio-group UI (only video tiles remain)
  preSdk,
}) {
  const [sdk, setSdk] = useState(null);
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState({ localParticipant: null, remoteParticipants: [] });
  const [input, setInput] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMessageBox, setShowMessageBox] = useState(false);
  const [muteAll, setMuteAll] = useState(false);
  const [localUserName, setLocalUserName] = useState('');
  const [newMessage, setNewMessage] = useState(0);
  const [previousMessageLength, setPreviousMessageLength] = useState(0);
  const [mutedParticipants, setMutedParticipants] = useState({});
  const [localMic, setLocalMic] = useState(micStart);
  const [typingUsers, setTypingUsers] = useState([]);  // [{ id, ts }]
  const [micError, setMicError] = useState(null);      // transient device-error notice
  // Gates the onMicStateChange backward-sync until the mic state has settled on
  // connect — otherwise the backward-sync reads the not-yet-ready mic as "muted"
  // and clobbers toggleMicChat before connect finishes.
  // NOTE: the mic ALWAYS starts OFF. The legacy project.voiceChatMic "start mic
  // on" setting is intentionally NOT honored (it's being removed from the DB);
  // users opt in by tapping the mic button. micStart is therefore always false.
  const [initialMicApplied, setInitialMicApplied] = useState(false);

  // Tell the parent (AppLive) whenever someone-is-typing flips, so the
  // controlPanel chat icon can show the live typing dots.
  useEffect(() => {
    onTypingChange?.(typingUsers.length > 0);
  }, [typingUsers, onTypingChange]);

  // Auto-dismiss the mic-device error notice after a few seconds.
  useEffect(() => {
    if (!micError) return;
    const t = setTimeout(() => setMicError(null), 6000);
    return () => clearTimeout(t);
  }, [micError]);

  // Keep the SDK ref in sync with the SDK state for the unmount cleanup above.
  useEffect(() => { sdkRef.current = sdk; }, [sdk]);

  // Keep AppLive's `toggleMicChat` (the controlPanel mic button) synced to the
  // REAL mic state. Without this it drifts whenever the mic changes via a path
  // other than the button (startMicCall, toggleMicLocal), and then a button
  // click can flip toggleMicChat to a value that's a no-op against the actual
  // mic — "mute doesn't mute until you unmute then mute again".
  useEffect(() => {
    // Don't sync back until the initial mic intent has been applied — before
    // that, micMuted just reflects "not connected yet", not the user's choice.
    if (!initialMicApplied) return;
    const lp = participants.localParticipant;
    if (lp && typeof lp.micMuted === 'boolean') {
      onMicStateChange?.(!lp.micMuted);
    }
  }, [participants.localParticipant?.micMuted, onMicStateChange, initialMicApplied]);

  const inputRef = useRef(null);
  const typingTimersRef = useRef({});
  const lastTypingSentRef = useRef(0);
  const scrollBottomRef = useRef(null);
  // Mirror the live SDK into a ref so the unmount cleanup can reach it — the
  // cleanup closure captures `sdk` at mount time (null), so it can't see the
  // SDK that gets created later.
  const sdkRef = useRef(null);

  // On unmount, leave the LiveKit room. AppLive unmounts VoiceChatUI during a
  // stream reconnect (it flips loading=true / showUiBtn=false), which happens
  // in the same render batch as setRoomDisconnect(true) — so the roomDisconnect
  // effect never gets to run sdk.leave(). Without this cleanup the Room is
  // orphaned: still connected and still kept alive by LiveKit's native
  // reconnection, so it ping-pongs against the freshly-mounted instance's new
  // Room (same identity → mutual server-side kicks → DUPLICATE_IDENTITY).
  useEffect(() => {
    return () => {
      const s = sdkRef.current;
      if (s) { try { s.leave(); } catch (e) { /* room already gone */ } }
      // startMicDone is module-level so it survives this unmount. The
      // roomDisconnect effect (which also resets it) never runs on a
      // reconnect-driven unmount, so reset it here — otherwise the next mount
      // sees a stale `true` and skips startMicCall, leaving the mic off even
      // though the user's intent was on.
      startMicDone = false;
    };
  }, []);
  // Keep muteAll in a ref so the onParticipantUpdate callback always sees the current value
  const muteAllRef = useRef(muteAll);
  muteAllRef.current = muteAll;

  const styleChatBox = isMobile ? { position: 'absolute', zIndex: 1 } : {};

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      scrollBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  // SDK initialisation — runs when we have a way to get an SDK (either preSdk from the lobby,
  // or roomConnect=true signalling we should create one ourselves).
  useEffect(() => {
    if (sdk !== null) return;
    if (!preSdk && roomConnect !== true) return;

    // If the lobby already created+joined an SDK, adopt it. Skip the join().
    const adopted = !!preSdk;
    // apiBase + voiceToken: /liveKit/getToken requires the session voice token from
    // the admission ticket, and derives the room grant from ITS projectId rather
    // than from roomName. Without them the token fetch is rejected.
    //
    // `voiceToken` is a GETTER here, not a value. The token arrives with the ticket,
    // asynchronously, and App.js holds it in a module variable whose assignment does
    // not re-render — so a captured value would be `undefined` for the life of this
    // SDK instance. The SDK calls the getter per request instead.
    const chatSdk = preSdk || new StreamPixelVoiceChat(roomName, userName, voiceChat, avatar, { apiBase, voiceToken, livekitUrl });
    setSdk(chatSdk);
    setLocalUserName(userName);

    const wireListeners = () => {
      chatSdk.onMessage((msg) => {
        const time = new Date();
        setMessages(prev => [...prev, { ...msg, time }]);
        if (msg?.from && msg.from !== 'You') {
          setTypingUsers(prev => prev.filter(u => u.id !== msg.from));
          if (typingTimersRef.current[msg.from]) {
            clearTimeout(typingTimersRef.current[msg.from]);
            delete typingTimersRef.current[msg.from];
          }
        }
        scrollToBottom();
      });

      chatSdk.onTyping?.(({ from }) => {
        if (!from || from === userName) return;
        setTypingUsers(prev => {
          const now = Date.now();
          const others = prev.filter(u => u.id !== from);
          return [...others, { id: from, ts: now }];
        });
        // Clear this user from the typing list after 3s of silence.
        if (typingTimersRef.current[from]) clearTimeout(typingTimersRef.current[from]);
        typingTimersRef.current[from] = setTimeout(() => {
          setTypingUsers(prev => prev.filter(u => u.id !== from));
          delete typingTimersRef.current[from];
        }, 3000);
      });

      chatSdk.onParticipantUpdate((list) => {
        setParticipants(list);

        if (muteAllRef.current) {
          const muteMap = {};
          list.remoteParticipants.forEach(p => {
            muteMap[p.id] = true;
          });
          setMutedParticipants(muteMap);
        }
      });

      chatSdk.onMediaDeviceError?.((info) => {
        setMicError(info?.message || 'Microphone unavailable.');
      });
    };

    const setup = async () => {
      wireListeners();
      // Re-gate the backward-sync for this SDK until the initial mic intent
      // below has been applied.
      setInitialMicApplied(false);

      if (adopted) {
        // Pre-joined by the lobby — surface the existing participants snapshot immediately
        // so the chat UI doesn't render empty for the first second.
        try { chatSdk._emitParticipants(); } catch (e) {}
        handleRoomConnect(true);
        if (micStart && !startMicDone) {
          // Lobby already toggled mic per user's choice; mark done so we don't double-toggle.
          startMicDone = true;
        }
        // Lobby already applied the mic intent — open the backward-sync gate.
        setInitialMicApplied(true);
        return;
      }

      const result  = await chatSdk.join();
      handleRoomConnect(true);

      if(result.joined && micStart && !startMicDone){
        await startMicCall(chatSdk);
      }
      // Initial mic intent applied (enabled, or intentionally left off) — the
      // backward-sync may now reconcile toggleMicChat to the real mic state.
      setInitialMicApplied(true);
    };

    setup();
  }, [roomName, userName, voiceChat, avatar, roomConnect, preSdk, apiBase, voiceToken, livekitUrl]);

  // Blur input when cleared
  useEffect(() => {
    if (input.trim() === '' && document.activeElement === inputRef.current) {
      inputRef.current.blur();
    }
  }, [input]);


  const startMicCall = async (chatSdk)=>{
    if(chatSdk === null) return;
    try {
      await chatSdk.toggleMic(true);
      setLocalMic(true);
      startMicDone = true;
    } catch (e) {
      console.error('startMicCall: mic enable failed', e);
    }
  }

  // Prevent game / scene key events while typing
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (document.activeElement === inputRef.current && e.key !== 'Enter') {
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  // Room disconnect
  useEffect(() => {
    if (!roomDisconnect || sdk === null) return;

    const disconnectRoom = async () => {
      setLocalMic(false);
      startMicDone = false;
      try {
        await sdk.leave();
        setSdk(null);
      } catch (error) {
        console.error('Error leaving room:', error);
      }
    };

    disconnectRoom();
  }, [roomDisconnect, sdk]);

  // External mic toggle trigger. Also re-runs when the local participant first
  // becomes available, so a toggle issued before the room was ready (e.g. right
  // after a reconnect, before _emitParticipants has run) still gets applied
  // instead of being silently dropped.
  const localParticipantReady = !!participants.localParticipant;
  useEffect(() => {
    if (sdk !== null && participants.localParticipant) {
      toggleMic(participants.localParticipant.id);
    }
  }, [toggleMicChat, localParticipantReady]); // eslint-disable-line react-hooks/exhaustive-deps


/*
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    const isNewRemote = lastMessage
      && lastMessage.from !== 'You'
      && messages.length > previousMessageLength;

    if (scrollBottomRef.current) {
       scrollBottomRef.current.scrollTop = scrollBottomRef.current.scrollHeight;
    }
      

    if (isNewRemote) {
      setPreviousMessageLength(messages.length);

      const chatVisible = isMobile ? showChatUiMobile : showMessageBox;
      if (!chatVisible) {
        setNewMessage(prev => {
          const next = prev + 1;
          handleMessageNumber(next);
          return next;
        });
      }
    }

    const chatVisible = isMobile ? showChatUiMobile : showMessageBox;
    if (chatVisible) {
      setNewMessage(0);
      handleMessageNumber(0);
    }
  }, [messages, showMessageBox, showChatUiMobile]);

*/

useEffect(() => {
  if (scrollBottomRef.current) {
    scrollBottomRef.current.scrollTop = scrollBottomRef.current.scrollHeight;
  }

  const lastMessage = messages[messages.length - 1];
  const panelVisible = isMobile ? showChatUiMobile : showChatUiDesktop;

  // New message arrived from someone else → bump unread count if the panel is closed.
  if (lastMessage && lastMessage.from !== 'You' && messages.length > previousMessageLength) {
    setPreviousMessageLength(messages.length);
    if (!panelVisible) {
      setNewMessage(newMessage + 1);
      handleMessageNumber(newMessage + 1);
    }
  }

  // Panel is open → user has seen it. Clear unread.
  if (panelVisible && newMessage !== 0) {
    setNewMessage(0);
    handleMessageNumber(0);
  }
 }, [messages, showChatUiDesktop, showChatUiMobile]);



  const sendMessage = () => {
    if (!sdk || !input.trim()) return;

    sdk.sendMessage(input.trim());
    setShowMessageBox(true);
    setInput('');
    inputRef.current?.blur();

    const videoElement = document.getElementById('videoElement');
    videoElement?.focus();
  };

  const muteAllRemote = () => {
    sdk.muteAllRemote();
    const updated = {};
    participants.remoteParticipants.forEach(p => {
      updated[p.id] = true;
    });
    setMutedParticipants(updated);
    setMuteAll(true);
  };

  const unmuteAllRemote = () => {
    sdk.unmuteAllRemote();
    const updated = {};
    participants.remoteParticipants.forEach(p => {
      updated[p.id] = false;
    });
    setMutedParticipants(updated);
    setMuteAll(false);
  };

  const muteSelected = (identity) => {

    sdk.muteSelected(identity);
    setMutedParticipants(prev => ({ ...prev, [identity]: true }));
  };

  const unmuteSelected = (identity) => {
    sdk.unmuteSelected(identity);
    setMutedParticipants(prev => ({ ...prev, [identity]: false }));
  };

  const toggleMic = async (participantId) => {
    if (participantId === localUserName) {
      // Awaited so it's not fire-and-forget. The SDK re-emits the real
      // participant state on both success and failure, so onMicStateChange
      // reconciles toggleMicChat to reality — nothing more to do here.
      await sdk.toggleMic(toggleMicChat);
    }
  };

  const toggleMicLocal = (participantId) => {
    if (participantId === localUserName) {


      sdk.toggleMic(!localMic);
      setLocalMic(prev => !prev);
    }
  };

  const onEmojiClick = (emojiData) => {
    setInput(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  return (
    <>
      {/* Camera tiles — hidden while the mobile chat panel is open, since the
          full-screen panel would otherwise sit underneath the floating tiles
          (they'd overlap the chat). Desktop is unaffected; the panel is a side
          card there. */}
      {sdk && videoChat && !(isMobile && showChatUiMobile) && (
        <VideoTilesUI sdk={sdk} participants={participants} darkMode={darkMode} position={position === 'Left' ? 'Left' : 'Right'} />
      )}
      {(textChat || voiceChat) && streamReady && (
        <ChatPanel
          // Chat view
          showChat={!!textChat}
          messages={messages}
          selfKey="You"
          onSendMessage={(text) => sdk && sdk.sendMessage(text)}
          onTyping={() => {
            const now = Date.now();
            if (now - lastTypingSentRef.current < 1500) return;
            lastTypingSentRef.current = now;
            sdk?.sendTyping?.();
          }}
          typingUsers={typingUsers}
          newMessages={newMessage}
          // People view (audio group folded in)
          showPeople={!!voiceChat}
          participants={participants.remoteParticipants}
          mutedParticipants={mutedParticipants}
          onToggleParticipant={(id, nextMuted) => (nextMuted ? muteSelected(id) : unmuteSelected(id))}
          muteAll={muteAll}
          onToggleMuteAll={(nextAll) => (nextAll ? muteAllRemote() : unmuteAllRemote())}
          // Chrome
          isOpen={isMobile ? showChatUiMobile : showChatUiDesktop}
          onClose={isMobile ? onCloseChatUi : onCloseChatUiDesktop}
          title={textChat ? 'Chat' : 'People'}
          isMobile={isMobile}
          darkMode={darkMode}
          position={position === 'Left' ? 'Left' : 'Right'}
        />
      )}

      {/* Message toasts — only when the chat panel is collapsed AND chat is
          enabled. Desktop: top-left (clear of top-right video tiles). Mobile:
          bottom corner by the controlPanel chat icon, sliding in from that edge. */}
      {textChat && streamReady && (
        <MessageToasts
          messages={messages}
          selfKey="You"
          active={isMobile ? !showChatUiMobile : !showChatUiDesktop}
          onClick={() => {
            if (isMobile) onOpenChatUi?.();
            else          onOpenChatUiDesktop?.();
          }}
          darkMode={darkMode}
          isMobile={isMobile}
          position={position === 'Left' ? 'Left' : 'Right'}
        />
      )}

      {/* Transient device-error notice (mic in use / permission denied / no
          device). Tap to dismiss; also auto-dismisses after a few seconds. */}
      {micError && (
        <div
          onClick={() => setMicError(null)}
          style={{
            position: 'fixed',
            top: '16px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2147483647,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 14px',
            borderRadius: '10px',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            maxWidth: '90vw',
            background: darkMode ? 'rgba(44,44,48,0.96)' : 'rgba(255,255,255,0.97)',
            color: darkMode ? '#ff8a80' : '#c62828',
            border: `1px solid ${darkMode ? 'rgba(255,138,128,0.35)' : 'rgba(198,40,40,0.25)'}`,
            boxShadow: '0 6px 24px rgba(0,0,0,0.28)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <span aria-hidden="true">⚠</span>
          <span>{micError}</span>
        </div>
      )}
    </>
  );
}
